import { useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Flag from '@atlaskit/flag';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { HandleDismissedType } from 'src/redux/flags/types';
import {
  getCurrentRepositoryFullSlug,
  getRepositoryAccessLevel,
} from 'src/selectors/repository-selectors';
import { atlassianStore as store } from 'src/utils/store';

import { messages } from './default-branch-deletion-flag.i18n';

// Key for storing dismissal state in local storage
const DEFAULT_BRANCH_DELETION_FLAG_DISMISSED_KEY =
  'default-branch-deletion-flag-dismissed';

type Props = {
  id: string;
} & HandleDismissedType;

/**
 * Flag component to notify admins about the new default branch deletion feature
 * Only renders if the feature flag is enabled and user has appropriate admin rights
 */
export const DefaultBranchDeletionFlag = (props: Props) => {
  const { id, handleDismissed } = props;
  const intl = useIntl();

  // access level defined by RepositoryPrivilege type:
  // 'admin' | 'read' | 'write' | 'none'
  const repositoryAccessLevel = useSelector(getRepositoryAccessLevel);
  const isFeatureEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcDefaultDeleteSourceBranchSetting
  );
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug);

  const [hasDismissedBranchDeletionFlag, setHasDismissedBranchDeletionFlag] =
    useState(!!store.get(DEFAULT_BRANCH_DELETION_FLAG_DISMISSED_KEY));
  // Check if the flag has been dismissed previously
  // Only show the flag if the user has repository admin rights, the feature flag is enabled,
  // and the user hasn't dismissed the flag previously
  if (
    !isFeatureEnabled ||
    repositoryAccessLevel !== 'admin' ||
    !repositoryFullSlug ||
    hasDismissedBranchDeletionFlag
  ) {
    return null;
  }

  const handleDismiss = () => {
    // Store dismissal in local storage
    store.set(DEFAULT_BRANCH_DELETION_FLAG_DISMISSED_KEY, true);
    setHasDismissedBranchDeletionFlag(
      store.get(DEFAULT_BRANCH_DELETION_FLAG_DISMISSED_KEY)
    );

    if (handleDismissed) {
      handleDismissed(id);
    }
  };

  return (
    <Flag
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      icon={
        <StatusInformationIcon
          spacing="spacious"
          color={token('color.icon.information')}
          label="Info"
        />
      }
      actions={[
        {
          content: 'Take me there',
          onClick: handleDismiss,
          href: `/${repositoryFullSlug}/admin/branching-model`,
        },
      ]}
      onDismissed={handleDismiss}
      {...props}
    />
  );
};
